import { useCallback, useEffect, useRef, useState } from "react";
import { UnauthorizedError } from "./api.ts";

export type Load<T> =
	| { state: "loading" }
	| { state: "ready"; data: T }
	| { state: "failed"; why: string };

/**
 * One request, with a manual refresh.
 *
 * The fetcher is held in a ref so callers can pass an inline arrow without the
 * effect re-firing on every render, and the mounted flag keeps a late response
 * from setting state on a screen the operator has already navigated away from.
 */
export function useLoad<T>(fn: () => Promise<T>): [Load<T>, () => void] {
	const [result, setResult] = useState<Load<T>>({ state: "loading" });
	const fnRef = useRef(fn);
	fnRef.current = fn;
	const mounted = useRef(true);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const run = useCallback(() => {
		setResult({ state: "loading" });
		fnRef
			.current()
			.then((data) => {
				if (mounted.current) setResult({ state: "ready", data });
			})
			.catch((e) => {
				// A 401 has already redirected into the OIDC flow; nothing to report.
				if (!mounted.current || e instanceof UnauthorizedError) return;
				setResult({ state: "failed", why: e instanceof Error ? e.message : String(e) });
			});
	}, []);

	useEffect(() => {
		run();
	}, [run]);

	return [result, run];
}
